import type { Beat } from "./director";
import type { VisualKind } from "./visuals";

/**
 * Anivaze v4 — conversational beat editing (PRODUCT-CHAT-EDIT §4).
 *
 * Because a beat is a STRUCTURED object, most "just change X" edits are a local
 * data mutation — no re-generation, ~$0, deterministic. This parses a plain
 * instruction for the SELECTED beat and returns the edited beat + a note. Only
 * "reword/simpler" genuinely needs a one-beat regeneration (flagged). This is the
 * moat vs. diffusion tools, where any tweak re-renders the whole clip.
 */

export interface EditResult {
  beat: Beat;
  changed: boolean;
  note: string;
  /** True when the ask needs a one-beat LLM regeneration (reword/simplify). */
  needsRegen?: boolean;
  /** True when the new visual is an image that must (re)fetch its asset. */
  fetchAsset?: boolean;
}

const KIND_WORDS: Record<string, VisualKind> = {
  chart: "chart", graph: "chart", donut: "chart", bar: "chart",
  rhythm: "rhythm", music: "rhythm", beat: "rhythm", waveform: "rhythm",
  flow: "flow", diagram: "flow", steps: "flow", process: "flow",
  pipeline: "pipeline", timeline: "flow",
  architecture: "archflow", system: "archflow", stack: "archflow",
  network: "network", web: "network",
  cycle: "cycle", loop: "cycle",
  image: "image", picture: "image", photo: "image", scene: "image", illustration: "image",
  sticker: "sticker",
};

export function applyBeatEdit(beat: Beat, instruction: string): EditResult {
  const s = (instruction ?? "").trim();
  if (!s) return { beat, changed: false, note: "" };

  // 1) change the background / image / picture to X → an image asset of X
  let m = s.match(/(?:change|replace|set|make|use|put)\s+(?:the\s+)?(?:background|image|picture|photo|scene|visual)(?:\s+(?:to|with|into|of|as))?\s+(.+)/i);
  if (m) {
    const subject = m[1].trim().replace(/^(a|an|the)\s+/i, "").replace(/[."']+$/, "");
    return {
      beat: { ...beat, visual: { ...beat.visual, kind: "image", items: [subject], assetUri: undefined } },
      changed: true, fetchAsset: true, note: `Background → “${subject}”`,
    };
  }

  // 2) make it a <kind>
  m = s.match(/(?:make\s+(?:it|this)|turn\s+(?:it|this)\s+into|render\s+(?:it|this)\s+as|show\s+(?:it|this)\s+as)\s+(?:(?:an|a)\s+)?([a-z]+)/i);
  if (m && KIND_WORDS[m[1].toLowerCase()]) {
    const kind = KIND_WORDS[m[1].toLowerCase()];
    return { beat: { ...beat, visual: { ...beat.visual, kind } }, changed: true, note: `Visual → ${kind}` };
  }

  // 3) add a symbol / icon / label / node X → append to the diagram items
  m = s.match(/^add\s+(?:a|an|the)?\s*(?:symbol|icon|label|item|node|box|step)?\s*(?:for|of|named|called|labelled|:)?\s*(.+)/i);
  if (m && m[1].trim()) {
    const label = m[1].trim().replace(/[."']+$/, "");
    const items = [...(beat.visual.items ?? []), label].slice(0, 6);
    return { beat: { ...beat, visual: { ...beat.visual, items } }, changed: true, note: `Added “${label}”` };
  }

  // 4) rename / change title to X
  m = s.match(/(?:rename|change\s+(?:the\s+)?title|set\s+(?:the\s+)?title|call\s+it|retitle)(?:\s+to)?\s+(.+)/i);
  if (m) {
    const title = m[1].trim().replace(/^["']|["']$/g, "").replace(/[.]+$/, "");
    return { beat: { ...beat, title }, changed: true, note: `Title → “${title}”` };
  }

  // 5) reword / simpler / shorter → needs a one-beat regeneration
  if (/\b(reword|rephrase|simpl(er|ify)|shorter|clearer|rewrite|explain (it )?better|make (it|this) (simpler|shorter))\b/i.test(s)) {
    return { beat, changed: false, needsRegen: true, note: "Rewording — regenerating this beat…" };
  }

  return { beat, changed: false, note: "Try: “change the image to a volcano”, “make it a chart”, “add solar panel”, or “rename to …”." };
}
